// Global Instructions Rule Applied!

import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../state_manager/hooks/useAppDispatch'
import { useDrawer } from '../../../core/contexts/DrawerContext.jsx'
import { ProspectRecord } from '../../../state_manager/interfaces/fundraising.ts'
import { fetchFundraisingById } from '../services/fundraisingService.ts'
import { selectProspectById } from '../../../state_manager/state_update_actions/fundraising/selectors.ts'
import { selectActiveRecordById } from '../../../state_manager/state_update_actions/active_records/selectors.ts'
import {
  setActiveRecord,
  registerRecordUsage
} from '../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { updateTask } from '../../../store/slices/tasksSlice'
import { TaskRecordFields } from '../../../state_manager/data_models/task_record.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'

/**
 * Custom hook for managing drawer state and operations for fundraising
 * Handles drawer opening, closing, and form submission logic
 */
export const useFundraisingDrawer = (prospects: ProspectRecord[]) => {
  const {
    isOpen: isDrawerOpen,
    recordId,
    isNew,
    openDrawerForNew,
    openDrawerForRecord,
    closeDrawer,
    formType
  } = useDrawer()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()

  const [selectedProspect, setSelectedProspect] = useState<ProspectRecord | null>(null)
  const hasAttemptedFetch = useRef(false)
  const fundraisingState = useSelector((state: any) => state.fundraising)

  const handleOpenDrawerForNew = useCallback(() => {
    setSelectedProspect(null)
    openDrawerForNew()
  }, [openDrawerForNew])

  const handleOpenDrawerForProspect = useCallback(
    (prospectId: string | number) => {
      if (!prospectId) {
        console.error('handleOpenDrawerForProspect called with undefined/null prospectId')
        return
      }
      const prospect = prospects.find((p) => p.id?.toString() === prospectId.toString())
      setSelectedProspect(prospect || null)
      openDrawerForRecord(prospectId)
    },
    [prospects, openDrawerForRecord]
  )

  const handleProspectNameClick = useCallback(
    (key: string, value: any, prospectId: string | number) => {
      if (prospectId) {
        handleOpenDrawerForProspect(prospectId)
      }
    },
    [handleOpenDrawerForProspect]
  )

  // Handle tasks click to open drawer with tasks focus
  const handleTasksClick = useCallback(
    (tasks: any[], prospectRecord: ProspectRecord, taskId: string | null = null) => {
      // Extract prospect ID from the prospect record passed from the table
      const prospectId = prospectRecord?.id

      if (prospectId) {
        // Build query parameters
        const params = new URLSearchParams()

        // If specific task ID is provided, add it to open that task's detail panel
        if (taskId) {
          params.set('taskId', taskId)
        }

        // Open drawer with or without query parameters
        const queryString = params.toString()
        navigate(`/fundraising/forms/drawer/${prospectId}${queryString ? `?${queryString}` : ''}`)
      }
    },
    [navigate]
  )

  // Handle task completion toggle
  const handleTaskToggle = useCallback(
    async (taskId: string, prospectId: string, completed: boolean) => {
      try {
        // Get prospect record from Redux state or prospects array to find the complete task
        let prospectRecord: ProspectRecord | null | undefined = selectProspectById(
          { fundraising: fundraisingState },
          prospectId
        )

        // Fallback to prospects array if not found in Redux state
        if (!prospectRecord) {
          prospectRecord = prospects.find((p) => p.id?.toString() === prospectId.toString()) || null
        }

        if (!prospectRecord) {
          console.error('Prospect record not found for task toggle')
          return
        }

        // Get tasks from prospect record (check both field names)
        const prospectTasks = prospectRecord['prog__tasks'] || prospectRecord['tasks'] || []
        const existingTask = prospectTasks.find(
          (t: any) => t[TaskRecordFields.tasks__id]?.toString() === taskId.toString()
        )

        if (!existingTask) {
          console.error('Task not found in prospect record')
          return
        }

        // Merge existing task data with completion update
        const completeTaskData = {
          ...existingTask,
          [TaskRecordFields.tasks__id]: taskId,
          [TaskRecordFields.tasks__completed]: completed
        }

        // Remove relationship fields - they shouldn't be sent when just updating completion
        delete completeTaskData['task_deals__deal_id']
        delete completeTaskData['task_deals__id']
        delete completeTaskData['task_accounts__account_id']
        delete completeTaskData['task_accounts__id']
        delete completeTaskData['task_prospects__prospect_id']
        delete completeTaskData['task_prospects__id']

        // Update backend - the updateTask thunk will update prospect record state
        await dispatch(
          updateTask({
            dealId: prospectId,
            taskId,
            taskData: completeTaskData,
            recordType: 'prospect'
          })
        ).unwrap()
      } catch (error) {
        console.error('Failed to update task:', error)
      }
    },
    [dispatch, fundraisingState, prospects]
  )

  const handleDrawerCancel = useCallback(() => {
    setSelectedProspect(null)
    closeDrawer()
  }, [closeDrawer])

  useEffect(() => {
    hasAttemptedFetch.current = false
  }, [recordId])

  // Get prospect from activeRecords first
  const prospectFromActiveRecords = useSelector((state: any) =>
    recordId && !isNew ? selectActiveRecordById(state, recordId) : null
  )

  useEffect(() => {
    const isFundraisingRoute =
      formType === 'prospect' ||
      location.pathname.startsWith('/fundraising') ||
      location.pathname.startsWith('/prospects')

    if (!isFundraisingRoute) {
      return
    }

    const currentToken = getAccessToken(store.getState().auth)
    if (recordId && !isNew && currentToken) {
      // First check activeRecords
      if (prospectFromActiveRecords) {
        setSelectedProspect(prospectFromActiveRecords)
        hasAttemptedFetch.current = true
        return
      }

      const prospect = prospects.find((p) => p.id?.toString() === recordId.toString())

      if (prospect) {
        setSelectedProspect(prospect)
        hasAttemptedFetch.current = true
      } else if (!hasAttemptedFetch.current) {
        const prospectFromState = selectProspectById({ fundraising: fundraisingState }, recordId)

        if (prospectFromState) {
          setSelectedProspect(prospectFromState)
          hasAttemptedFetch.current = true
        } else {
          hasAttemptedFetch.current = true
          fetchFundraisingById(recordId, currentToken)
            .then((response) => {
              if (response.data && !response.error) {
                const fetchedProspect = response.data as ProspectRecord
                // Only add to activeRecords, not module state
                const prospectIdString = recordId.toString()
                dispatch(setActiveRecord({ uuid: prospectIdString, record: fetchedProspect }))
                dispatch(registerRecordUsage({ uuid: prospectIdString, context: 'drawer' }))
                setSelectedProspect(fetchedProspect)
              } else if (response.error) {
                console.error('Error fetching prospect:', response.error)
                setSelectedProspect(null)
              }
            })
            .catch((error) => {
              console.error('Error fetching prospect:', error)
              setSelectedProspect(null)
            })
        }
      }
    } else if (isNew || !recordId) {
      setSelectedProspect(null)
      hasAttemptedFetch.current = false
    }
  }, [recordId, isNew, prospects, dispatch, fundraisingState, prospectFromActiveRecords, formType, location.pathname])

  return {
    isDrawerOpen,
    recordId,
    isNew,
    selectedProspect,
    handleOpenDrawerForNew,
    handleOpenDrawerForProspect,
    handleProspectNameClick,
    handleTasksClick,
    handleTaskToggle,
    handleDrawerCancel
  }
}
